import React, { useEffect, useMemo, useState } from 'react';
import { Resolver, useForm, useWatch } from 'react-hook-form';
import { useHotkeys } from 'react-hotkeys-hook';
import { isEmpty } from 'lodash';
import { useLocation, useNavigate, useParams } from 'react-router-dom';

import { CreateCompanyFormFieldValues } from '@modules/companies/create-company/types';
import {
  createCompanyFormDefaultValues,
  SectionMapper
} from '@modules/companies/create-company/constants';
import { yupResolver } from '@hookform/resolvers/yup';
import { createCompanySchema } from '@modules/companies/create-company/validate';
import { MODAL_TYPES } from '@modules/syndicate/create-syndicate/constants';
import { API_RESPONSE_STATUS, PreIPODealStatus } from '@constants/common';
import { CompletedStepperMap } from '@components/stepper/types';
import { useSelector } from 'react-redux';
import { RootState } from '@store/reducers';
import { CustomModal } from '@components';
import { translate, translateDynamic } from '@utils/locale';
import { findErrorTabIndex } from '@utils/common';
import { RoutePath } from '@routes/index';
import { showGenericErrorToast } from '@utils/error';
import { Backdrop, CircularProgress } from '@mui/material';
import { useAppDispatch } from '@store/store';
import { updateBreadCrumb } from '@reducers/breadCrumb';
import { ToastTypes } from '@type/toast';
import CompanyInfoForm from '@modules/companies/create-company/containers/CompanyInfoForm';
import useCreateCompany from '@modules/companies/create-company/hooks/useCreateCompany';
import TeamMembersTable from '@modules/companies/create-company/containers/TeamMembersTable';
import InvestorsTable from '@modules/companies/create-company/containers/InvestorsTable';
import ProductSection from '@modules/companies/create-company/containers/ProductSection';
import CompetitorsSection from '@modules/companies/create-company/containers/CompetitorsSection';
import useTimeZone from '@hooks/useTimeZone';
import ValuationHeader from '@modules/companies/create-company/components/valuation-header/ValuationHeader';
import ValuationsTimeline from '@modules/companies/create-company/containers/ValuationsTimelineContainer';
import PublishSuccessModal from '@modules/deals/deals-list/components/publish-success-modal/PublishSuccessModal';
import DefaultColorChip from '@components/default-color-chip/DefaultColorChip';
import displayToast from '@components/toast/customToast';

import { PreIPODealFormValues } from './types';
import {
  PRE_IPO_FIELD_TO_TAB_MAPPER,
  PRE_IPO_FIELD_TO_VALUE_MAPPER,
  preIPODealFormDefaultValues
} from './constants';
import { preIPODealSchema } from './validate';
import {
  useCreatePreIPOMutation,
  useLazyGetPreIPOQuery,
  useUpdatePreIPOMutation
} from './api';
import {
  formatCreatePreIPOApi,
  formatGetPreIPOApi,
  formatUpdatePreIPOApi
} from './services';
import { preIPOStatusColorMapping } from '../pre-ipo-deals-section/common';
import PreIPODealConfiguration from './containers/pre-ipo-deal-configuration/PreIPODealConfiguration';
import CreatePreIPODealPageHeader from './containers/CreatePreIPODealPageHeader';
import PreIPODealsCompanySection from './containers/PreIPODealsCompanySection';
import PreIPODealDetailsForm from './containers/PreIPODealDetailsForm';
import PreIPODataRoom from './containers/PreIPODataRoom';
import PreIPOTransactionDetails from './containers/pre-ipo-transaction-details/PreIPOTransactionDetails';

const CreatePreIPODealPage = () => {
  const { dealId, companyId, cloneId } = useParams();
  const location = useLocation();

  const createCompletedStatusMap = (numberOfKeys: number) => {
    const obj: CompletedStepperMap = {};
    for (let index = 0; index < numberOfKeys; index++) {
      obj[index] = false;
    }
    return obj;
  };

  const [isPublishClicked, setIsPublishClicked] = useState(false);
  const [isDraftClicked, setIsDraftClicked] = useState<boolean>(false);
  const [isModalOpen, setIsModalOpen] = useState<string>('');
  const [activeStep, setActiveStep] = useState(2);
  const [editMode, setEditMode] = useState<SectionMapper>();
  const [stepCompletionStatus] = useState<CompletedStepperMap>(
    createCompletedStatusMap(5)
  );
  const [statusToPublish, setStatusToPublish] = useState<PreIPODealStatus>(
    PreIPODealStatus.IN_REVIEW
  );

  const createPreIPODealState = location.pathname.includes('create');

  const { companyId: companyID } = useSelector(
    (state: RootState) => state.rootReducer.createPreIPODealStates
  );

  const [
    getPreIPO,
    {
      isLoading: isGetPreIPOLoading,
      isFetching: isGetPreIPOFetching,
      data: getPreIPORes
    }
  ] = useLazyGetPreIPOQuery();

  const updateEditMode = (section?: SectionMapper) => setEditMode(section);

  const {
    getResetFunction,
    isLoading,
    countriesList,
    timeZoneList,
    companyDetails,
    getResetAddressSection,
    currencyList,
    getResetLegalInfoSection,
    getResetBrandSection,
    sectors,
    companyConfiguration,
    getResetProductSection,
    getResetCompetitors,
    opportunityList
  } = useCreateCompany();

  const preIPODealData = useMemo(() => {
    return {
      lastUpdatedAt: getPreIPORes?.last_updated_at
    };
  }, [getPreIPORes]);

  const createCompanyMethods = useForm<CreateCompanyFormFieldValues>({
    defaultValues: createCompanyFormDefaultValues,
    resolver: yupResolver(createCompanySchema) as never as Resolver<
      CreateCompanyFormFieldValues,
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      any
    >
  });

  const preIPODealMethods = useForm<PreIPODealFormValues>({
    defaultValues: preIPODealFormDefaultValues,
    resolver: yupResolver(
      preIPODealSchema
    ) as never as Resolver<PreIPODealFormValues>,
    mode: 'onSubmit'
  });

  const {
    formState: { errors, isDirty: isPreIPODealFormDirty },
    getValues,
    setValue,
    handleSubmit,
    trigger,
    control,
    setError,
    reset
  } = preIPODealMethods;

  const status = useWatch({
    control,
    name: 'status'
  });

  //API calls
  const [
    updatePreIPO,
    {
      data: updatePreIPOResponse,
      isSuccess: updatePreIPOSuccess,
      isLoading: isUpdatePreIPOLoading,
      error
    }
  ] = useUpdatePreIPOMutation();

  const [
    createPreIPO,
    {
      data: createPreIPOResponse,
      error: createPreIPOError,
      isError,
      isSuccess,
      isLoading: isCreatePreIPOLoading
    }
  ] = useCreatePreIPOMutation();

  useEffect(() => {
    if (isError) showGenericErrorToast(createPreIPOError);
  }, [isError]);

  useEffect(() => {
    if (isSuccess && createPreIPOResponse) {
      reset(formatGetPreIPOApi(createPreIPOResponse.result, true));
    }
  }, [createPreIPOResponse, isSuccess]);

  useEffect(() => {
    if (updatePreIPOSuccess && updatePreIPOResponse) {
      reset(formatGetPreIPOApi(updatePreIPOResponse.result, true));
      if (updatePreIPOResponse?.result.name)
        dispatch(
          updateBreadCrumb({
            label: 'Edit Deal',
            newLabel: `Edit ${updatePreIPOResponse?.result.name}`
          })
        );
    }
  }, [updatePreIPOResponse, updatePreIPOSuccess]);

  const [isPublishSuccess, setIsPublishSuccess] = useState<boolean>(false);
  const navigate = useNavigate();
  const publishDeal = async () => {
    if (getValues('id')) {
      let payload = formatUpdatePreIPOApi(
        getValues(),
        statusToPublish,
        getUtcDateTimeWithTimeZone,
        getUtcEndDateWithTimeZone
      );
      try {
        const response = await updatePreIPO(payload).unwrap();
        if (response && response.status === API_RESPONSE_STATUS.OK) {
          setIsModalOpen('');
          setIsPublishSuccess(true);
          setTimeout(() => {
            setIsPublishSuccess(false);
            navigate(RoutePath.PRE_IPO_DEALS);
          }, 3000);
        }
        setStatusToPublish(PreIPODealStatus.IN_REVIEW);
      } catch (err) {
        showGenericErrorToast(err);
      }
    } else {
      let payload = formatCreatePreIPOApi(
        getValues(),
        PreIPODealStatus.IN_REVIEW,
        getUtcDateTimeWithTimeZone,
        getUtcEndDateWithTimeZone
      );
      try {
        const response = await createPreIPO(payload).unwrap();
        if (response && response.status === API_RESPONSE_STATUS.OK) {
          setIsModalOpen('');
          setIsPublishSuccess(true);
          setTimeout(() => {
            setIsPublishSuccess(false);
            navigate(RoutePath.PRE_IPO_DEALS);
          }, 3000);
        } else showGenericErrorToast(createPreIPOError);
      } catch (err) {
        showGenericErrorToast(err);
      }
    }
  };

  const handlePublishPreIPO = () => {
    setIsModalOpen(MODAL_TYPES.publish);
  };
  const handlePublishClick = (preIPOStatus: PreIPODealStatus) => {
    setValue('isPublish', true);
    setStatusToPublish(preIPOStatus);
    handleSubmit(handlePublishPreIPO)();
    setIsPublishClicked(true);
  };
  const handleReviewClick = () => {
    const tabToRedirect = findErrorTabIndex(
      errors,
      PRE_IPO_FIELD_TO_TAB_MAPPER
    );
    if (tabToRedirect > -1) {
      setActiveStep(tabToRedirect);
    }
    setIsModalOpen('');
    setIsPublishClicked(false);
    setIsDraftClicked(false);
    setValue('isPublish', false);
    setValue('isDraft', true);
    setStatusToPublish(PreIPODealStatus.IN_REVIEW);
  };
  useHotkeys(
    'enter',
    () =>
      isModalOpen === MODAL_TYPES.error ? handleReviewClick() : publishDeal(),
    { enabled: !!isModalOpen }
  );

  const stepClickHandler = async (index: number) => {
    const isValid = await trigger(
      PRE_IPO_FIELD_TO_TAB_MAPPER[
        activeStep
      ] as never as keyof PreIPODealFormValues
    );

    if (isValid) {
      setActiveStep(index);
    } else {
      setActiveStep(activeStep);
    }
  };
  const { getUtcDateTimeWithTimeZone, getUtcEndDateWithTimeZone } =
    useTimeZone();

  const resetDuplicateData = async () => {
    try {
      const dealResponse = await getPreIPO({ dealId: cloneId }).unwrap();
      reset(formatGetPreIPOApi(dealResponse, false));
    } catch (err) {
      showGenericErrorToast(err);
    }
  };

  useEffect(() => {
    if (cloneId) {
      resetDuplicateData();
    }
  }, [cloneId]);

  const handleSaveAsDraft = () => {
    if (getValues('id')) {
      updatePreIPO(
        formatUpdatePreIPOApi(
          getValues(),
          PreIPODealStatus.DRAFT,
          getUtcDateTimeWithTimeZone,
          getUtcEndDateWithTimeZone
        )
      );
    } else
      createPreIPO(
        formatCreatePreIPOApi(
          getValues(),
          PreIPODealStatus.DRAFT,
          getUtcDateTimeWithTimeZone,
          getUtcEndDateWithTimeZone
        )
      );
    if (activeStep === 5) setActiveStep(2);
  };

  const handleSaveAsDraftClick = () => {
    setValue('isPublish', false);
    setValue('isDraft', true);
    handleSubmit(handleSaveAsDraft)();
    setIsDraftClicked(true);
  };
  const [isDealEditPage, setIsDealEditPage] = useState<boolean>(false);
  const dispatch = useAppDispatch();
  const resetEditData = async () => {
    setIsDealEditPage(true);
    try {
      const dealResponse = await getPreIPO({ dealId: dealId }).unwrap();
      reset(formatGetPreIPOApi(dealResponse, true));
      if (dealResponse.name)
        dispatch(
          updateBreadCrumb({
            label: 'Edit Pre-IPO Deal',
            newLabel: `Edit ${dealResponse.name}`
          })
        );
    } catch (err) {
      showGenericErrorToast(err);
    }
  };

  useEffect(() => {
    if (dealId) {
      resetEditData();
    } else if (companyID) {
      setValue('companyId', companyID);
    } else if (companyId) {
      setValue('companyId', companyId);
    } else {
      navigate(RoutePath.PRE_IPO_DEALS);
    }
  }, [companyID, dealId, companyId]);

  const handleNextClick = async () => {
    const isValid = await trigger(
      PRE_IPO_FIELD_TO_TAB_MAPPER[
        activeStep
      ] as never as keyof PreIPODealFormValues
    );

    if (isValid) {
      handleSaveAsDraftClick();
      setActiveStep(prev => prev + 1);
    } else {
      setActiveStep(activeStep);
    }
  };

  const onProductEditButtonClick = (section: SectionMapper) => {
    setEditMode(section);
  };
  useEffect(() => {
    //@ts-ignore
    if (error?.data?.error?.details.includes('conflicts with current data')) {
      displayToast(
        ToastTypes.ERROR,
        'This pre-ipo deal has been updated recently, reloading the page in 1s.'
      );
      setTimeout(() => {
        window.location.reload();
      }, 1000);
    }
  }, [error]);
  const handleCancelClick = () => {
    setIsModalOpen('');
    setIsPublishClicked(false);
    setIsDraftClicked(false);
    setValue('isPublish', false);
    setValue('isDraft', true);
    setStatusToPublish(PreIPODealStatus.IN_REVIEW);
  };

  const renderEachStepperData = () => {
    switch (activeStep) {
      case 0:
        return (
          <>
            <CompanyInfoForm
              methods={createCompanyMethods}
              editMode={editMode}
              updateEditMode={updateEditMode}
              isLoading={isLoading}
              companyId={companyId ?? ''}
              countriesList={countriesList}
              timeZoneList={timeZoneList}
              companyDetails={companyDetails}
              getResetAddressSection={getResetAddressSection}
              getResetFunction={getResetFunction}
              currencyList={currencyList}
              getResetLegalInfoSection={getResetLegalInfoSection}
              getResetBrandSection={getResetBrandSection}
              sectors={sectors}
              companyConfiguration={companyConfiguration}
            />
            <div className="flex flex-col mx-5 mt-5">
              <hr />
              <div className="mb-14">
                <TeamMembersTable />
              </div>
              <hr />
              <div className="mb-14 w-[70%]">
                <InvestorsTable
                  showVisibleDealColumn={false}
                  showEmptyState={true}
                />
              </div>
              <div className="mt-4 mb-12 w-full">
                <ProductSection
                  getResetFunction={getResetFunction}
                  onClickEditButton={onProductEditButtonClick}
                  editMode={editMode}
                  updateEditMode={updateEditMode}
                  disableEdit={false}
                  companyDetails={companyDetails}
                  getResetProductSection={getResetProductSection}
                />
              </div>
              <hr />
              <div className="mt-4 mb-20 w-full">
                <CompetitorsSection
                  getResetFunction={getResetFunction}
                  onClickEditButton={onProductEditButtonClick}
                  editMode={editMode}
                  updateEditMode={updateEditMode}
                  disableEdit={false}
                  companyDetails={companyDetails}
                  getResetCompetitors={getResetCompetitors}
                />
              </div>
              <hr />
              <div className="mt-4 mb-20 w-full">
                <PreIPODealsCompanySection
                  disableEdit={false}
                  editMode={editMode}
                  updateEditMode={updateEditMode}
                  getResetFunction={getResetFunction}
                  companyDetails={companyDetails}
                />
              </div>
              <hr />
            </div>
          </>
        );
      case 1:
        return (
          <div className="p-5">
            <ValuationHeader />
            <ValuationsTimeline />
          </div>
        );
      case 2:
        return (
          <PreIPODealDetailsForm
            methods={preIPODealMethods}
            errors={errors}
            handleNextClick={handleNextClick}
            isUpdatePreIPOLoading={
              isUpdatePreIPOLoading || isCreatePreIPOLoading
            }
            handleSaveAsDraftClick={handleSaveAsDraftClick}
            isGetPreIPOLoading={isGetPreIPOLoading || isGetPreIPOFetching}
          />
        );
      case 3:
        return (
          <PreIPODataRoom
            handleSubmit={handleSubmit}
            errors={errors}
            methods={preIPODealMethods}
            handleSaveAsDraftClick={handleSaveAsDraftClick}
            handleNextClick={handleNextClick}
            isUpdateCreateDealLoading={
              isUpdatePreIPOLoading || isCreatePreIPOLoading
            }
          />
        );
      case 4:
        return (
          <PreIPODealConfiguration
            methods={preIPODealMethods}
            opportunityList={opportunityList}
            errors={errors}
            handleSaveAsDraftClick={handleSaveAsDraftClick}
            handleNextClick={handleNextClick}
            isUpdatePreIPOLoading={
              isUpdatePreIPOLoading || isCreatePreIPOLoading
            }
          />
        );
      case 5:
        if (status === '')
          return (
            <PreIPOTransactionDetails
              methods={preIPODealMethods}
              control={control}
              setValue={setValue}
              errors={errors}
              handleSaveAsDraftClick={handleSaveAsDraftClick}
              setError={setError}
              isUpdatePreIPOLoading={
                isUpdatePreIPOLoading || isCreatePreIPOLoading
              }
            />
          );
        return <></>;
      default:
        return <></>;
    }
  };

  useEffect(() => {
    if (
      (!isEmpty(errors) && isPublishClicked) ||
      (!isEmpty(errors) && isDraftClicked)
    ) {
      setIsModalOpen(MODAL_TYPES.error);
    }
  }, [errors]);

  const getErrorDetailsUI = () => {
    return (
      !isEmpty(errors) && (
        <div className="overflow-auto max-h-[600px]">
          {Object.keys(errors).map(item => (
            <div
              key={item}
              className="pb-[9px] pl-10 text-base font-normal text-black/80">
              {PRE_IPO_FIELD_TO_VALUE_MAPPER[item]}
            </div>
          ))}
        </div>
      )
    );
  };

  const getPublishModalBody = () => {
    return (
      <>
        <div className="flex flex-col items-start px-6 pb-4 w-full border-b">
          <div className="text-xl font-medium">
            {translate('preIpoDeals.createPreIPODeals.modal.publishTitle')}
          </div>
          <div className="text-[14px] font-light">
            {translate(
              'preIpoDeals.createPreIPODeals.modal.publishDescription'
            )}
          </div>
        </div>
        <div className="flex flex-col gap-4 p-4 m-4 bg-lightGrey4 rounded-sm border">
          <div className="flex flex-row gap-4 justify-between">
            <div>
              {translate(
                'preIpoDeals.createPreIPODeals.modal.publishPreIPOName'
              )}
            </div>
            <div>
              <span
                className="inline-block mx-1 max-w-[200px] truncate align-middle"
                title={getValues('preIPOName')}>
                {getValues('preIPOName')}
              </span>
            </div>
          </div>
          {status && (
            <div className="flex flex-row gap-4 justify-between">
              <div>
                {translate('preIpoDeals.createPreIPODeals.modal.currentStatus')}
              </div>
              <div>
                <DefaultColorChip
                  eachEntry={status}
                  colorMapping={preIPOStatusColorMapping}
                  labelValue={translateDynamic(`preIPODealStatus.${status}`)}
                  variant={
                    status === PreIPODealStatus.ACTIVE ||
                    status === PreIPODealStatus.EXPIRED
                      ? 'filled'
                      : 'outlined'
                  }
                />
              </div>
            </div>
          )}
          <div className="flex flex-row gap-4 justify-between">
            <div>
              {translate('preIpoDeals.createPreIPODeals.modal.newStatus')}
            </div>
            <div>
              <DefaultColorChip
                eachEntry={statusToPublish}
                colorMapping={preIPOStatusColorMapping}
                labelValue={translateDynamic(
                  `preIPODealStatus.${statusToPublish}`
                )}
                variant={
                  statusToPublish === PreIPODealStatus.ACTIVE ||
                  statusToPublish === PreIPODealStatus.EXPIRED
                    ? 'filled'
                    : 'outlined'
                }
              />
            </div>
          </div>
        </div>
      </>
    );
  };

  return (
    <>
      <CreatePreIPODealPageHeader
        isEditPage={isDealEditPage}
        status={getValues('status')}
        isPreIPODealFormDirty={isPreIPODealFormDirty}
        handlePublish={handlePublishClick}
        lastUpdatedAt={preIPODealData.lastUpdatedAt}
        activeStep={activeStep}
        handleStepClick={stepClickHandler}
        stepCompletionStatus={stepCompletionStatus}
        dealName={
          (updatePreIPOResponse?.result.name ?? '') ||
          (getPreIPORes?.name ?? '')
        }
        createPreIPODealState={createPreIPODealState}
      />
      {renderEachStepperData()}
      <CustomModal
        paddingLeft={0}
        paddingRight={0}
        buttongroupStyles="px-4 pt-4"
        isModalOpen={!!isModalOpen}
        header={
          isModalOpen === MODAL_TYPES.error ? (
            <div className="px-6">
              {translate('modals.incompleteDetailsPreIPOModalTitle')}
            </div>
          ) : (
            ''
          )
        }
        primaryButtonLabel={translate(
          isModalOpen === MODAL_TYPES.error
            ? 'modals.reviewButtonTitle'
            : 'publish',
          { syndicateName: getValues('preIPOName') || '' }
        )}
        secondaryButtonVariant="text"
        secondaryButtonLabel={translate('modals.cancelButtonTitle')}
        body={
          isModalOpen === MODAL_TYPES.error
            ? getErrorDetailsUI()
            : getPublishModalBody()
        }
        onChangePrimaryButton={
          isModalOpen === MODAL_TYPES.error ? handleReviewClick : publishDeal
        }
        onChangeSecondaryButton={handleCancelClick}
        isPrimaryButtonLoading={isUpdatePreIPOLoading || isCreatePreIPOLoading}
      />
      {isPublishSuccess && (
        <PublishSuccessModal
          dealName={getValues('preIPOName')}
          isModalOpen={isPublishSuccess}
        />
      )}
      <Backdrop
        sx={{ color: '#fff', zIndex: theme => theme.zIndex.drawer + 1 }}
        open={isGetPreIPOLoading}>
        <CircularProgress color="inherit" />
      </Backdrop>
    </>
  );
};
export default CreatePreIPODealPage;
